import { createSelector } from 'reselect'

import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'

type FlightBaggageState = NonNullable<App.CheckoutState['flightBaggage']>

/**
 * How long after fetching the offers a purchase may still be attempted.
 *
 * svc-flights caches the offers for 30 minutes (`models/redis/Addons.ts`, `TTL = 30 * 60`) and its
 * addon create reads that cache rather than re-fetching from the provider — unlike CABIN_UPGRADE,
 * which refreshes first. Deliberately shorter than 30: the server's clock starts when it writes the
 * cache, slightly before our response lands, and clock skew is not ours to assume away.
 */
export const BAGGAGE_OFFER_WINDOW_MS = 25 * 60 * 1000

/**
 * What the customer pays for post-booking bags.
 *
 * These are the provider's own amounts, summed and nothing more. svc-flights records the cached
 * option's `price.amount` as the addon price and svc-order's `validateAddonPayments` checks only
 * the payment plan's status, never the amount — so nothing anywhere reconciles this against what
 * is recorded. Any adjustment here charges one number and books another, silently.
 */
export function getFlightBaggageTotalPrice(flightBaggage: FlightBaggageState): number {
  return flightBaggage.selections.reduce((total, selection) => total + selection.price, 0)
}

export const getFlightBaggageTotal = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.flightBaggage,
  (postPurchaseMode, flightBaggage): number => {
    if (postPurchaseMode !== 'flight-baggage' || !flightBaggage) { return 0 }
    return getFlightBaggageTotalPrice(flightBaggage)
  },
)

export function buildFlightBaggageAddonInputs(
  flightBaggage: FlightBaggageState,
): Array<App.FlightBaggageAddonInput> {
  return flightBaggage.selections.map(selection => ({
    type: 'BAGGAGE',
    payload: {
      travellerId: selection.travellerId,
      productCode: selection.productCode,
      legIndex: selection.legIndex,
      // ATPCO has no offer item id at all, and svc-order's schema rejects an unknown shape,
      // so the key is omitted rather than sent as undefined.
      ...(selection.offerItemId ? { offerItemId: selection.offerItemId } : {}),
    },
  }))
}

export function isFlightBaggageOfferWindowExpired(fetchedAt: number, now: number = Date.now()): boolean {
  return now - fetchedAt >= BAGGAGE_OFFER_WINDOW_MS
}
